import { useState, useEffect, useRef } from "react";
import { BookAppointmentModal } from "components/BookAppointment";
import DoctorCarousel from "components/DoctorCarousel";
import HeroReadMore from "components/HeroReadMore";
import scrollToSection from "utils/scrollToSection";
import mapsUrl from "utils/mapsUrl";
import visibleStats from "utils/visibleStats";
import AddressesModal from "components/site/AddressesModal";
import buildImgTransform from "utils/imgTransform";
import PhoneIcon from "components/site/PhoneIcon";
import { useParallax, Masonry, ChromaVeil, chromaTrack, SpotlightCard } from "components/site/fx";
import { StarBorder } from "components/site/textfx";
import SplashCursor from "components/site/SplashCursor";
import GalleryCarousel from "components/site/GalleryCarousel";

const PLACEHOLDER_HERO = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=1600&q=80";
const PLACEHOLDER_GALLERY = [
  "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80",
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=900&q=80",
  "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=900&q=80",
  "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=900&q=80",
];

function ImageCarousel({ images, accent, title }) {
  if (!images || images.length === 0) return null;
  return (
    <section style={{ padding: '72px 24px', background: '#f0f7f2' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <p style={{ color: '#1e3a2f', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Gallery</p>
          <h2 style={{ fontSize: 'clamp(22px, 2.8vw, 34px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: '#1a1a1a' }}>{title || 'Our Clinic'}</h2>
        </div>
        <GalleryCarousel images={images} accent={accent} />
      </div>
    </section>
  );
}

const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const buildWhatsAppHref = (cfg) => {
  if (!cfg?.whatsapp_enabled || !cfg?.contact_phone) return null;
  const digits = String(cfg.contact_phone).replace(/\D/g, '');
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  return cfg.whatsapp_message
    ? `${base}?text=${encodeURIComponent(cfg.whatsapp_message)}`
    : base;
};

// ── Palette ─────────────────────────────────────────────────────────────
const W = {
  forest: '#1e3a2f',       // hero bg, testimonials bg, footer bg
  forestLight: '#274d3b',  // hover states
  sage: '#eef5ee',         // alternating section bg
  white: '#ffffff',
  textDark: '#1a1a1a',
  textMid: '#374151',
  textMuted: '#6b7280',
  border: 'rgba(30,58,47,0.15)',
};

// ── Shared SVGs ──────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const LeafIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
  </svg>
);

function StarRating({ rating, color }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill={s <= rating ? color : '#ddd'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

// ── Condition enrichment ─────────────────────────────────────────────────
const COND_LIBRARY = [
  { keys: ['skin','eczema','psoriasis','acne','dermatit','urticaria','vitiligo'], badge: 'Dermatology', period: '4–8 weeks', ages: 'All ages' },
  { keys: ['hair','alopecia','baldness','dandruff','scalp'], badge: 'Trichology', period: '8–16 weeks', ages: 'Adults' },
  { keys: ['asthma','bronchitis','cough','respiratory','sinusit','rhinitis','allerg'], badge: 'Respiratory', period: '6–10 weeks', ages: 'All ages' },
  { keys: ['anxiety','stress','depression','insomnia','sleep','mental','mind'], badge: 'Mind & Body', period: '8–12 weeks', ages: 'Adults' },
  { keys: ['digest','ibs','colitis','gastrit','bloating','constipat','diarrhea','acidity'], badge: 'Digestive', period: '6–10 weeks', ages: 'All ages' },
  { keys: ['joint','arthrit','rheumat','back pain','spine','knee','shoulder'], badge: 'Musculoskeletal', period: '8–14 weeks', ages: 'Adults' },
  { keys: ['thyroid','diabetes','hormone','hormonal','metabol','obesity','weight'], badge: 'Metabolic', period: '12–20 weeks', ages: 'Adults' },
  { keys: ['child','pediatr','infant','kid','baby','growth'], badge: 'Paediatrics', period: '4–8 weeks', ages: 'Children' },
  { keys: ['women','menstrual','pcos','pcod','menopause','fertility','gynae','leucorrhoea'], badge: "Women's Health", period: '8–16 weeks', ages: 'Women' },
  { keys: ['migraine','headache','vertigo','dizzi','neurolog'], badge: 'Neurology', period: '8–12 weeks', ages: 'Adults' },
  { keys: ['kidney','urin','prostat','uti'], badge: 'Urology', period: '8–12 weeks', ages: 'Adults' },
  { keys: ['heart','cardiac','cholesterol','blood pressure','hypertension'], badge: 'Cardiology', period: '12–24 weeks', ages: 'Adults' },
];
const ICONS_COND = ['🌿','🌱','💊','🧘','🫁','🦷','🩺','🏃','❤️','🧠','💧','🌸'];
function enrichCondition(str) {
  const s = str.toLowerCase();
  for (const row of COND_LIBRARY) {
    if (row.keys.some(k => s.includes(k))) return row;
  }
  return { badge: 'General', period: '4–8 weeks', ages: 'All ages' };
}

// ── Reveal on scroll ────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0) scale(1)'; e.target.style.filter = 'blur(0)'; } }),
      { threshold: 0.12 }
    );
    const items = ref.current.querySelectorAll('[data-reveal]');
    items.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

const revealStyle = { opacity: 0, transform: 'translateY(32px) scale(0.98)', filter: 'blur(5px)', transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(.2,.7,.2,1), filter 0.7s ease' };

export default function TemplateWellness({ config, companyData }) {
  const [heroRef, heroParallax] = useParallax(0.18);
  const [modalOpen, setModalOpen] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [preselect, setPreselect] = useState({ doctorId: null, specId: null });
  const openBookWith = (doctorId, specId) => { setPreselect({ doctorId, specId }); setModalOpen(true); };
  const [navScrolled, setNavScrolled] = useState(false);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [dark, setDark] = useState(false);
  const [hoveredCond, setHoveredCond] = useState(null);
  const [hoveredWhy, setHoveredWhy] = useState(null);
  const [hoveredApproach, setHoveredApproach] = useState(null);
  const [bioDoc, setBioDoc] = useState(null); // doctor card showing full bio (hover/tap)
  const pageRef = useReveal();

  const acc = config.accentColor;
  const testimonials = config.testimonials || [];
  const heroBg = config.heroBannerImage || PLACEHOLDER_HERO;
  const whatsappHref = buildWhatsAppHref(config);
  const galleryImages = (config.galleryImages || []).filter(Boolean);
  const carouselImages = (config.galleryCarousel || []).filter(Boolean);
  const imgAdjust = config.imageAdjust || {};
  const doctorsList = (config.doctors || []).filter((d) => d.is_active && d.show_on_website !== false);
  const doctorsSection = config.doctorsSection || { enabled: true, title: 'Meet Our Doctors' };

  const scrollToId = (id) => {
    if (id === 'w-home') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    scrollToSection(id);
  };

  // Dark mode overrides for light sections
  const lightBg = dark ? '#0f1a12' : W.white;
  const sageBg = dark ? '#0a1309' : W.sage;
  const textDark = dark ? '#e8f0e9' : W.textDark;
  const textMid = dark ? '#a0b8a3' : W.textMid;
  const textMuted = dark ? '#608060' : W.textMuted;
  const cardBg = dark ? '#142018' : W.white;
  const border = dark ? 'rgba(255,255,255,0.07)' : W.border;

  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!testimonials.length) return;
    const timer = setInterval(() => setTestimonialIdx((i) => (i + 1) % testimonials.length), 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  return (
    <div ref={pageRef} style={{ fontFamily: "'DM Sans', ui-sans-serif, system-ui, sans-serif", color: textDark, background: lightBg, transition: 'background 0.3s, color 0.3s' }}>
      <style>{`
        html { scroll-behavior: smooth; }
        section[id^="w-"] { scroll-margin-top: 84px; }
        @media (max-width: 820px) {
          .wellness-nav-links { display: none !important; }
          .w-nav-extra { display: none !important; }
          .w-hero { flex-direction: column !important; min-height: auto !important; }
          .w-hero-left, .w-hero-right { flex: 1 1 100% !important; max-width: 100% !important; }
          .w-hero-right { min-height: 320px !important; }
        }
      `}</style>

      {/* ── Sticky Nav ────────────────────────────────────────────────── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: navScrolled ? 'rgba(255,255,255,0.97)' : W.white,
        borderBottom: navScrolled ? `1px solid ${W.border}` : '1px solid transparent',
        boxShadow: navScrolled ? '0 2px 16px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s, border-color 0.3s',
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', height: 68 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: W.forest, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
              {config.logo
                ? <img src={config.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: config.logoTransform }} />
                : <LeafIcon />}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: W.textDark, lineHeight: 1.1 }}>{config.companyName}</div>
              {config.industry_category_name && (
                <div style={{ fontSize: '0.65rem', color: W.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{config.industry_category_name}</div>
              )}
            </div>
          </div>

          {/* Nav links */}
          <div style={{ display: 'flex', gap: 4, margin: '0 20px' }} className="wellness-nav-links">
            {['About', 'Services', 'Testimonials', 'Contact'].filter((l) => !((l === 'Testimonials' && testimonials.length === 0) || (l === 'Services' && !config.whyChooseUs))).map((l) => (
              <a key={l} href={`#w-${l.toLowerCase()}`} onClick={(e) => { e.preventDefault(); scrollToId(`w-${l.toLowerCase()}`); }} style={{ fontSize: '0.9rem', color: W.textMid, textDecoration: 'none', fontWeight: 500, padding: '7px 15px', borderRadius: 999, transition: 'color 0.25s, background 0.25s, transform 0.25s' }}
                onMouseOver={(e) => { e.target.style.color = '#fff'; e.target.style.background = acc; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseOut={(e) => { e.target.style.color = W.textMid; e.target.style.background = 'transparent'; e.target.style.transform = 'none'; }}>
                {l}
              </a>
            ))}
          </div>

          {/* CTA */}
          {whatsappHref && (
            <a className="w-nav-extra" href={whatsappHref} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 12, background: '#25d366', color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
              <WhatsAppIcon size={15} /> WhatsApp
            </a>
          )}
          <button className="w-nav-extra" onClick={() => setDark(!dark)}
            style={{ width: 36, height: 36, borderRadius: '50%', background: dark ? '#e8f0e9' : '#1e3a2f', color: dark ? '#1e3a2f' : '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
            {dark ? '☀' : '🌙'}
          </button>
          <button onClick={() => setModalOpen(true)}
            style={{ background: W.forest, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Book Consultation
          </button>
        </div>
      </nav>

      <BookAppointmentModal
        id={companyData?.company_id}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setPreselect({ doctorId: null, specId: null }); }}
        onSuccess={() => {}}
        accentColor={acc}
        preselectedDoctorId={preselect.doctorId}
        preselectedSpecId={preselect.specId}
      />
      <AddressesModal open={addrOpen} onClose={() => setAddrOpen(false)} locations={config.locations || []} accent={acc} />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="w-hero" style={{ display: 'flex', minHeight: '88vh', overflow: 'hidden' }}>
        {/* Left — content with background image */}
        <div className="w-hero-left" style={{ flex: '0 0 52%', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* parallax background image */}
          <img ref={heroRef} src={heroBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: `${heroParallax} scale(1.15)`, transition: 'transform 0.1s linear' }} onError={e => { e.target.src = PLACEHOLDER_HERO; }} />
          {/* dark overlay */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(20,46,32,0.85)' }} />
          <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(48px, 8vw, 96px) clamp(24px, 5vw, 80px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '6px 14px', width: 'fit-content', marginBottom: 28 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80' }} />
            <span style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 500 }}>Online &amp; Offline Consultations</span>
          </div>

          <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.1, fontFamily: "'Larken', Georgia, serif" }}>
            {config.tagline?.split(' ').slice(0, 2).join(' ') || 'Heal Naturally.'}
          </h1>
          <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 700, color: acc, margin: '4px 0 24px', lineHeight: 1.1, fontFamily: "'Larken', Georgia, serif" }}>
            {config.tagline?.split(' ').slice(2).join(' ') || 'Breathe Easy.'}
          </h1>

          <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)', lineHeight: 1.75, margin: '0 0 36px', maxWidth: 460 }}>
            {config.heroSubtext}
          </p>
          <HeroReadMore paragraphs={config.heroExtraParagraphs} heading={config.companyName} linkColor="#fff" />


          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <StarBorder color={acc} radius={10}>
              <button onClick={() => setModalOpen(true)}
                style={{ background: 'transparent', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 28px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}>
                Book Consultation
              </button>
            </StarBorder>
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 28px', fontSize: '0.95rem', fontWeight: 600, textDecoration: 'none' }}>
                <WhatsAppIcon size={16} /> WhatsApp
              </a>
            )}
          </div>
          </div>{/* end inner content wrapper */}
        </div>{/* end left panel */}

        {/* Right — solid dark panel */}
        <div className="w-hero-right" style={{ flex: '0 0 48%', position: 'relative', overflow: 'hidden', minHeight: 480, background: 'linear-gradient(160deg, #0f2d1e 0%, #081a10 100%)' }}>

          {/* Content layer */}
          <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: 40 }}>

            {/* Logo glass card */}
            <div style={{ backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 18, padding: config.logo ? '10px 18px' : 18, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
              {config.logo ? (
                <img src={config.logo} alt={config.companyName} style={{ height: 56, maxWidth: 160, objectFit: 'contain', filter: 'brightness(1.15)' }} />
              ) : (
                <div style={{ color: acc, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LeafIcon />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.02em' }}>{config.companyName}</span>
                </div>
              )}
            </div>

            {/* Center headline */}
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 10 }}>Trusted Healing</p>
              <h3 style={{ color: '#fff', fontSize: 'clamp(20px, 2.8vw, 30px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", lineHeight: 1.35, margin: 0 }}>
                Healing Naturally.<br />Trusted Results.
              </h3>
              <div style={{ width: 42, height: 3, background: acc, borderRadius: 2, margin: '18px auto 0' }} />
            </div>

            {/* Stat chips row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {[
                ...visibleStats(config, { bookings: 'Bookings', doctors: 'Doctors', users: 'Patients' }).map((s) => ({ key: s.key, v: `${s.val}+`, l: s.label })),
                { key: 'hours', v: config.consultationHours || 'Mon–Sat', l: 'Hours' },
              ].map((s) => (
                <div key={s.key} style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 40, padding: '8px 18px', display: 'flex', gap: 6, alignItems: 'baseline', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  <span style={{ fontSize: '1.05rem', fontWeight: 700, color: acc }}>{s.v}</span>
                  <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>{s.l}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ── About / Legacy ───────────────────────────────────────────── */}
      <section id="w-about" style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: lightBg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 'clamp(32px, 5vw, 80px)', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Left — photo grid */}
          <div data-reveal style={{ ...revealStyle, flex: '0 0 clamp(260px, 42%, 460px)' }}>
            <Masonry images={(galleryImages.length > 0 ? galleryImages : PLACEHOLDER_GALLERY).slice(0, 5)} columns={2} gap={10} radius={14} accent={acc} />
          </div>

          {/* Right — text */}
          <div data-reveal style={{ ...revealStyle, flex: '1 1 300px', transitionDelay: '0.1s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: W.forest, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Our Legacy</span>
              <div style={{ height: 2, width: 40, background: acc, borderRadius: 2 }} />
            </div>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: W.textDark, margin: '0 0 20px', lineHeight: 1.2 }}>
              {config.aboutWhoWeAre ? config.companyName : `Excellence in ${config.companyName}`}
            </h2>
            <p style={{ color: W.textMid, lineHeight: 1.8, marginBottom: 16 }}>{config.aboutWhoWeAre}</p>
            {config.aboutWhoWeAreBullets?.length > 0 && (
              <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {config.aboutWhoWeAreBullets.map((b, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, color: W.textMid, fontSize: '0.95rem' }}>
                    <span style={{ color: W.forest, flexShrink: 0 }}><CheckIcon /></span>
                    {b}
                  </li>
                ))}
              </ul>
            )}
            {config.aboutWhoWeAreNote && <p style={{ color: W.textMuted, fontStyle: 'italic', fontSize: '0.9rem', lineHeight: 1.7 }}>{config.aboutWhoWeAreNote}</p>}
          </div>
        </div>
      </section>

      {/* ── Meet Our Doctors ─────────────────────────────────────────── */}
      {doctorsSection.enabled && doctorsList.length > 0 && (
        <section onMouseMove={chromaTrack} style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: lightBg, position: 'relative' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...revealStyle, textAlign: 'center', marginBottom: 40 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: acc }}>Our Team</span>
              <h2 style={{ fontSize: 'clamp(24px, 3.2vw, 38px)', fontWeight: 700, color: textDark, marginTop: 12, fontFamily: "'Larken', Georgia, serif" }}>
                {doctorsSection.title || 'Meet Our Doctors'}
              </h2>
            </div>
            <DoctorCarousel accent={acc}>
              {doctorsList.map((d, i) => (
                <div key={d.id} data-reveal style={{ ...revealStyle, transitionDelay: `${i * 0.05}s`, position: 'relative', background: cardBg, borderRadius: 18, overflow: 'hidden', border: `1px solid ${border}`, display: 'flex', flexDirection: 'column', transition: 'transform 0.22s, box-shadow 0.22s' }}
                  >
                  <div style={{ aspectRatio: '4 / 3', background: sageBg, position: 'relative', overflow: 'hidden' }}>
                    {d.photo ? (
                      <img src={d.photo} alt={d.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: buildImgTransform({ scale: d.photo_scale, position: d.photo_position }), transformOrigin: 'center' }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', fontWeight: 800, color: W.forest, opacity: 0.35, fontFamily: "'Larken', Georgia, serif" }}>
                        {d.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '20px 22px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: textDark, marginBottom: 4, fontFamily: "'Larken', Georgia, serif" }}>{d.name}</h3>
                    {d.qualifications && <div style={{ fontSize: '0.78rem', color: textMuted, marginBottom: 8 }}>{d.qualifications}</div>}
                    {d.specialization_name && (
                      <div style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 700, color: acc, background: `${acc}14`, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                        {d.specialization_name}
                      </div>
                    )}
                    {d.experience_years > 0 && (
                      <div style={{ fontSize: '0.85rem', color: textMid, marginBottom: 8 }}>
                        <span style={{ color: acc, fontWeight: 700 }}>{d.experience_years}+</span> {d.experience_years === 1 ? 'year' : 'years'} of experience
                      </div>
                    )}
                    {d.bio && <p style={{ fontSize: '0.85rem', color: textMid, lineHeight: 1.6, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{d.bio}</p>}
                    {d.bio && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setBioDoc((cur) => (cur === d.id ? null : d.id)); }}
                        style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 18 }}>
                        View full bio →
                      </button>
                    )}
                    <button onClick={() => openBookWith(d.id, d.specialization)}
                      style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '10px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                      Book Appointment →
                    </button>
                  </div>

                  {/* Full-bio reveal — covers the card on hover/tap, reverts on mouse-leave */}
                  {d.bio && (
                    <div style={{ position: 'absolute', inset: 0, background: cardBg, padding: 22, display: 'flex', flexDirection: 'column', gap: 8, opacity: bioDoc === d.id ? 1 : 0, pointerEvents: bioDoc === d.id ? 'auto' : 'none', transition: 'opacity 0.25s', overflowY: 'auto' }}>
                      <button type="button" onClick={() => setBioDoc(null)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: acc, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: 0, marginBottom: 2 }}>← Back</button>
                      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: textDark, fontFamily: "'Larken', Georgia, serif", margin: 0 }}>{d.name}</h3>
                      {d.specialization_name && <div style={{ fontSize: '0.7rem', fontWeight: 700, color: acc, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{d.specialization_name}</div>}
                      <p style={{ fontSize: '0.84rem', color: textMid, lineHeight: 1.65, margin: '4px 0 0', whiteSpace: 'pre-line' }}>{d.bio}</p>
                      <button onClick={() => openBookWith(d.id, d.specialization)}
                        style={{ marginTop: 'auto', background: acc, color: '#fff', border: 'none', borderRadius: 100, padding: '10px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                        Book Appointment →
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </DoctorCarousel>
          </div>
          <ChromaVeil />
        </section>
      )}

      {/* ── Why Choose Us ─────────────────────────────────────────────── */}
      {config.whyChooseUs && (
        <section id="w-services" style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: sageBg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...revealStyle, textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: W.textDark, margin: '0 0 12px' }}>
                Why Choose Us
              </h2>
              <p style={{ color: W.textMuted, maxWidth: 500, margin: '0 auto', lineHeight: 1.7 }}>
                A patient-first approach combined with clinical excellence
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
              {config.whyChooseUs.map((point, i) => (
                <div key={i} data-reveal
                  onMouseEnter={() => setHoveredWhy(i)}
                  onMouseLeave={() => setHoveredWhy(null)}
                  style={{ ...revealStyle, transitionDelay: `${i * 0.07}s`, position: 'relative', overflow: 'hidden', background: hoveredWhy === i ? W.forest : cardBg, borderRadius: 18, padding: '28px 24px 24px', border: `1.5px solid ${hoveredWhy === i ? W.forest : border}`, cursor: 'default', transition: 'background 0.25s, border-color 0.25s, transform 0.2s, box-shadow 0.22s', transform: hoveredWhy === i ? 'translateY(-6px) scale(1.02)' : 'none', boxShadow: hoveredWhy === i ? `0 16px 40px rgba(30,58,47,0.25)` : '0 2px 8px rgba(0,0,0,0.05)' }}>
                  {/* Top gradient accent strip */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${W.forest}, ${acc})`, borderRadius: '18px 18px 0 0', opacity: hoveredWhy === i ? 0 : 1, transition: 'opacity 0.25s' }} />
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${acc}, rgba(255,255,255,0.5))`, borderRadius: '18px 18px 0 0', opacity: hoveredWhy === i ? 1 : 0, transition: 'opacity 0.25s' }} />
                  {/* Watermark number */}
                  <div style={{ position: 'absolute', right: 16, top: 10, fontSize: '4.5rem', fontWeight: 900, color: hoveredWhy === i ? 'rgba(255,255,255,0.08)' : `${W.forest}0d`, lineHeight: 1, userSelect: 'none', transition: 'color 0.25s', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  {/* Icon circle */}
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: hoveredWhy === i ? 'rgba(255,255,255,0.16)' : `${W.forest}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: hoveredWhy === i ? '#fff' : W.forest, marginBottom: 14, transition: 'background 0.25s, color 0.25s' }}>
                    <CheckIcon />
                  </div>
                  <p style={{ color: hoveredWhy === i ? 'rgba(255,255,255,0.92)' : textMid, fontSize: '0.94rem', lineHeight: 1.65, margin: 0, fontWeight: hoveredWhy === i ? 500 : 400, transition: 'color 0.25s, font-weight 0.2s' }}>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Conditions ────────────────────────────────────────────────── */}
      {config.conditions && (
        <section style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: lightBg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...revealStyle, textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: W.textDark, margin: '0 0 12px' }}>
                Conditions We Treat
              </h2>
              {config.conditionsTagline && (
                <p style={{ color: W.textMuted, maxWidth: 500, margin: '0 auto', lineHeight: 1.7 }}>{config.conditionsTagline}</p>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              {config.conditions.map((cond, i) => {
                const meta = enrichCondition(cond);
                return (
                  <SpotlightCard key={i} glow={acc} radius={16} borderColor={border}
                    style={{ background: cardBg, borderTop: `3px solid ${W.forest}`, padding: '20px 18px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div data-reveal style={{ ...revealStyle, transitionDelay: `${i * 0.05}s`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {/* Badge */}
                      <span style={{ alignSelf: 'flex-start', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 9px', borderRadius: 20, background: `${W.forest}14`, color: W.forest }}>
                        {meta.badge}
                      </span>
                      {/* Icon + name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{ICONS_COND[i % ICONS_COND.length]}</span>
                        <span style={{ fontSize: '0.95rem', fontWeight: 600, color: textDark, lineHeight: 1.35 }}>{cond}</span>
                      </div>
                      {/* Meta chips */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                        <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20, background: `${W.forest}10`, color: W.textMuted, fontWeight: 500 }}>👤 {meta.ages}</span>
                      </div>
                    </div>
                  </SpotlightCard>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Our Approach ─────────────────────────────────────────────── */}
      {config.aboutApproach && (
        <section style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: sageBg }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div data-reveal style={{ ...revealStyle, textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: W.textDark, margin: '0 0 12px' }}>
                Our Approach
              </h2>
              <p style={{ color: W.textMuted, maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
                A holistic treatment philosophy focused on root-cause healing
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, justifyContent: 'center' }}>
              {config.aboutApproach.map((item, i) => (
                <div key={i} data-reveal
                  onMouseEnter={() => setHoveredApproach(i)}
                  onMouseLeave={() => setHoveredApproach(null)}
                  style={{ ...revealStyle, transitionDelay: `${i * 0.08}s`, background: hoveredApproach === i ? W.forest : cardBg, borderRadius: 16, padding: '28px 24px', border: `1.5px solid ${hoveredApproach === i ? W.forest : border}`, textAlign: 'center', cursor: 'default', transition: 'background 0.22s, transform 0.18s, box-shadow 0.2s', transform: hoveredApproach === i ? 'translateY(-5px)' : 'none', boxShadow: hoveredApproach === i ? '0 14px 36px rgba(30,58,47,0.22)' : '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: hoveredApproach === i ? acc : W.forest, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', transition: 'background 0.22s', boxShadow: hoveredApproach === i ? `0 4px 16px rgba(0,0,0,0.2)` : 'none' }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                  </div>
                  <p style={{ color: hoveredApproach === i ? 'rgba(255,255,255,0.9)' : textMid, fontSize: '0.95rem', lineHeight: 1.65, margin: 0, transition: 'color 0.22s' }}>{item}</p>
                </div>
              ))}
            </div>
            {config.aboutApproachNote && (
              <p data-reveal style={{ ...revealStyle, textAlign: 'center', color: W.textMuted, fontStyle: 'italic', marginTop: 32, lineHeight: 1.7 }}>{config.aboutApproachNote}</p>
            )}
          </div>
        </section>
      )}

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      {testimonials.length > 0 && (
        <section id="w-testimonials" style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: W.forest }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", color: '#fff', margin: '0 0 12px' }}>
                What Our Patients Say
              </h2>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
                <StarRating rating={5} color={acc} />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>
                {testimonials.length}+ verified patient reviews
              </p>
            </div>

            <div style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 20, transition: 'transform 0.5s ease', transform: `translateX(calc(-${testimonialIdx * (100 / Math.min(3, testimonials.length))}% - ${testimonialIdx * (20 / Math.min(3, testimonials.length))}px))` }}>
                {testimonials.map((t, i) => (
                  <div key={t.id || i} style={{ flex: `0 0 calc(${100 / Math.min(3, testimonials.length)}% - ${20 * (Math.min(3, testimonials.length) - 1) / Math.min(3, testimonials.length)}px)`, background: 'rgba(255,255,255,0.08)', borderRadius: 16, padding: '28px 24px', border: '1px solid rgba(255,255,255,0.12)', position: 'relative' }}>
                    <div style={{ fontSize: '2.5rem', color: acc, opacity: 0.6, lineHeight: 1, marginBottom: 12, fontFamily: "'Larken', Georgia, serif" }}>"</div>
                    <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.92rem', lineHeight: 1.75, margin: '0 0 20px' }}>{t.feedback}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <StarRating rating={t.rating || 5} color={acc} />
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', fontWeight: 600, marginTop: 12 }}>
                      {t.user__first_name} {t.user__last_name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {testimonials.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 28 }}>
                <button onClick={() => setTestimonialIdx((i) => (i - 1 + testimonials.length) % testimonials.length)}
                  style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <button onClick={() => setTestimonialIdx((i) => (i + 1) % testimonials.length)}
                  style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Gallery Carousel ── */}
      <ImageCarousel images={carouselImages.length > 0 ? carouselImages : PLACEHOLDER_GALLERY} accent={acc} title={config.galleryTitle} />

      {/* ── Mission / CTA ─────────────────────────────────────────────── */}
      <section id="w-contact" style={{ padding: 'clamp(48px, 7vw, 80px) 24px', background: lightBg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 'clamp(24px, 5vw, 60px)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* CTA card */}
          <div data-reveal style={{ ...revealStyle, flex: '1 1 320px', background: W.forest, borderRadius: 20, padding: '40px 36px', color: '#fff' }}>
            <h2 style={{ fontSize: 'clamp(22px, 2.5vw, 30px)', fontWeight: 700, fontFamily: "'Larken', Georgia, serif", margin: '0 0 16px', lineHeight: 1.3 }}>
              Start Your Healing Journey
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, marginBottom: 28 }}>
              {config.closingCta || `Book a consultation with ${config.companyName} today.`}
            </p>
            <button onClick={() => setModalOpen(true)}
              style={{ width: '100%', background: acc, color: '#fff', border: 'none', borderRadius: 10, padding: '15px 24px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
              Book Appointment
            </button>
          </div>

          {/* Info cards */}
          <div data-reveal style={{ ...revealStyle, flex: '1 1 260px', transitionDelay: '0.1s', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {config.contact_phone && (
              <div style={{ background: W.sage, borderRadius: 14, padding: '20px 24px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: acc, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: W.textDark, marginBottom: 4 }}>Contact</div>
                  <div style={{ color: W.textMuted, fontSize: '0.88rem' }}>{config.contact_phone}</div>
                </div>
              </div>
            )}
            {config.locations?.length > 0 && (
              <div style={{ background: W.sage, borderRadius: 14, padding: '20px 24px' }}>
                <div style={{ fontWeight: 600, color: W.textDark, marginBottom: 12 }}>
                  {config.locations.length === 1 ? 'Our Location' : `Our Locations (${config.locations.length})`}
                </div>
                {config.locations.map((loc, i) => {
                  const isObj = typeof loc === 'object' && loc !== null;
                  const nm = isObj ? (loc.name || `Branch ${i + 1}`) : loc;
                  const addr = isObj ? loc.address : '';
                  const ph = isObj ? loc.phone : '';
                  return (
                    <div key={isObj && loc.id ? loc.id : i} style={{ marginBottom: i < config.locations.length - 1 ? 12 : 0 }}>
                      <a href={mapsUrl(loc)} target="_blank" rel="noopener noreferrer" title={addr || nm} style={{ display: 'flex', alignItems: 'center', gap: 8, color: W.textDark, fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: W.forest, flexShrink: 0 }} />
                        {nm}
                      </a>
                      {ph && <a href={`tel:${ph}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 14, marginTop: 2, color: W.forest, fontSize: '0.82rem', textDecoration: 'none', fontWeight: 500 }}><PhoneIcon size={12} /> {ph}</a>}
                    </div>
                  );
                })}
                <button type="button" onClick={() => setAddrOpen(true)} style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, cursor: 'pointer', color: W.forest, fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>
                  ⓘ View full address{config.locations.length > 1 ? 'es' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Floating WhatsApp ── */}
      {whatsappHref && (
        <a href={whatsappHref} target="_blank" rel="noopener noreferrer" title="Chat on WhatsApp"
          style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 200, width: 52, height: 52, borderRadius: '50%', background: '#25d366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(37,211,102,0.45)', textDecoration: 'none' }}>
          <WhatsAppIcon size={26} />
        </a>
      )}

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer style={{ background: W.forest, padding: 'clamp(40px, 5vw, 60px) 24px 24px', color: 'rgba(255,255,255,0.75)', position: 'relative', overflow: 'hidden' }}>
        <SplashCursor colors={[acc, '#a7f3d0', '#ffffff']} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, paddingBottom: 32, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LeafIcon />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>{config.companyName}</div>
                </div>
              </div>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.7, maxWidth: 220 }}>{config.footerTagline}</p>
            </div>

            <div style={{ flex: '1 1 160px' }}>
              <h4 style={{ color: '#fff', fontWeight: 600, margin: '0 0 14px', fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Quick Links</h4>
              {['Home', 'About', 'Services', 'Testimonials', 'Contact'].filter((l) => !((l === 'Testimonials' && testimonials.length === 0) || (l === 'Services' && !config.whyChooseUs))).map((l) => (
                <a key={l} href={`#w-${l.toLowerCase()}`} onClick={(e) => { e.preventDefault(); scrollToId(`w-${l.toLowerCase()}`); }} style={{ display: 'block', color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', textDecoration: 'none', marginBottom: 8 }}>→ {l}</a>
              ))}
            </div>

            {config.conditions?.slice(0, 5).length > 0 && (
              <div style={{ flex: '1 1 160px' }}>
                <h4 style={{ color: '#fff', fontWeight: 600, margin: '0 0 14px', fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Our Specialties</h4>
                {config.conditions.slice(0, 5).map((c, i) => (
                  <div key={i} style={{ color: i === 0 ? acc : 'rgba(255,255,255,0.65)', fontSize: '0.87rem', marginBottom: 8 }}>→ {typeof c === 'string' ? c.split('(')[0].trim() : c}</div>
                ))}
              </div>
            )}

            <div style={{ flex: '1 1 180px' }}>
              <h4 style={{ color: '#fff', fontWeight: 600, margin: '0 0 14px', fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact Us</h4>
              {config.address && <p style={{ fontSize: '0.87rem', lineHeight: 1.6, marginBottom: 8 }}>{config.address}</p>}
              {config.contact_phone && <p style={{ fontSize: '0.87rem', marginBottom: 6 }}>📞 {config.contact_phone}</p>}
              {config.contact_email && <p style={{ fontSize: '0.87rem', marginBottom: 6 }}>✉ {config.contact_email}</p>}
              {config.consultationHours && <p style={{ fontSize: '0.87rem' }}>🕐 {config.consultationHours}</p>}
            </div>
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.82rem', marginTop: 20 }}>
            © {new Date().getFullYear()} {config.companyName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
